import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Alert, AlertTitle, Box, IconButton, InputAdornment, Stack, TextField, Tooltip } from '@mui/material';
import * as React from 'react';

import { DeveloperApiError } from '../api';
import { formatTs } from '../components';
import type { IntegrationProbe, ProductionKeyWarning } from '../integrationTypes';
import { PROBE_RESULTS } from './integrationLabels';

export interface CredentialField {
    key: string;
    label: string;
    secret: boolean;
    required: boolean;
    hint?: string | null;
    set?: boolean;
}

/** Per-key reasons of a refusal (`errors[] {key, message}`): shown under their field. */
export const fieldErrors = (error: unknown): Record<string, string> => {
    if (!(error instanceof DeveloperApiError)) {
        return {};
    }
    return Object.fromEntries(error.errors.filter((e) => e.key).map((e) => [e.key, e.message || e.code]));
};

/** Every required field has a value (the group is replaced as a whole). */
export const credentialsComplete = (fields: CredentialField[], values: Record<string, string>) => (
    fields.every((f) => !f.required || (values[f.key] || '').trim() !== '')
);

/** Only the keys typed in, trimmed. */
export const credentialsBody = (fields: CredentialField[], values: Record<string, string>) => (
    Object.fromEntries(fields.filter((f) => (values[f.key] || '').trim() !== '').map((f) => [f.key, values[f.key].trim()]))
);

/** Vendor credentials are not this site's login: password managers must neither offer to save them for the
 *  dashboard nor fill the user's own sign-in into them (an e-mail + password pair reads as a sign-up form). So no
 *  <form> around them, the opt-out attributes of 1Password, LastPass and Dashlane, and no `type=password` where
 *  the browser can draw text as discs. */
export const NO_PASSWORD_MANAGER = {
    autoComplete: 'off',
    'data-1p-ignore': 'true',
    'data-lpignore': 'true',
    'data-form-type': 'other',
} as const;

/** The browser draws text as discs (`-webkit-text-security`; read at render time, a browser either has it or not). */
export const textSecuritySupported = () => (
    typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('-webkit-text-security', 'disc')
);

/** A secret field's type and input attributes. Masked: text drawn as discs where the browser can; elsewhere a
 *  password field (`new-password`: never the user's own login), so a secret never shows in clear. Shown: text. */
export const secretFieldProps = (masked: boolean) => {
    const discs = masked && textSecuritySupported();
    const password = masked && !discs;
    const autoComplete = password ? 'new-password' : 'off';
    return {
        type: password ? 'password' : 'text',
        autoComplete,
        inputProps: {
            ...NO_PASSWORD_MANAGER,
            autoComplete,
            spellCheck: false,
            'data-masked': masked ? 'true' : 'false',
            style: discs ? { WebkitTextSecurity: 'disc' } as React.CSSProperties : undefined,
        },
    };
};

const SecretInput = ({ field, value, onChange, error, disabled, autoFocus }: {
    field: CredentialField; value: string; onChange: (v: string) => void; error?: string; disabled?: boolean; autoFocus?: boolean;
}) => {
    const [shown, setShown] = React.useState(false);
    const secret = secretFieldProps(field.secret && !shown);
    const helper = error || field.hint || (field.set ? 'Set. Type a new value to replace it: the stored one is never shown.' : undefined);
    return (
        <TextField
            size="small"
            fullWidth
            label={field.label}
            required={field.required}
            value={value}
            onChange={(ev) => onChange(ev.target.value)}
            error={!!error}
            helperText={helper}
            disabled={disabled}
            autoFocus={autoFocus}
            type={secret.type}
            autoComplete={secret.autoComplete}
            inputProps={{ ...secret.inputProps, 'data-testid': `credential-${field.key}` }}
            InputProps={field.secret ? {
                endAdornment: (
                    <InputAdornment position="end">
                        <Tooltip title={shown ? 'Hide' : 'Show what you typed'}>
                            <IconButton size="small" edge="end" aria-label={shown ? `Hide ${field.label}` : `Show ${field.label}`} onClick={() => setShown((s) => !s)}>
                                {shown ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                            </IconButton>
                        </Tooltip>
                    </InputAdornment>
                ),
            } : undefined}
        />
    );
};

/** The credential group (`x-auth-keys`), write-only: nothing is ever filled in from the server. */
const CredentialFields = ({ fields, values, onChange, errors = {}, disabled, autoFocus }: {
    fields: CredentialField[];
    values: Record<string, string>;
    onChange: (values: Record<string, string>) => void;
    errors?: Record<string, string>;
    disabled?: boolean;
    autoFocus?: boolean;
}) => (
    <Box
        sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}
        data-testid="credential-fields"
    >
        {fields.map((field, idx) => (
            <SecretInput
                key={field.key}
                field={field}
                value={values[field.key] || ''}
                onChange={(v) => onChange({ ...values, [field.key]: v })}
                error={errors[field.key]}
                disabled={disabled}
                autoFocus={autoFocus && idx === 0}
            />
        ))}
    </Box>
);

export default CredentialFields;

/** O16: these credentials are a production tenant's (a sandbox only). */
export const ProductionKeyWarnings = ({ items }: { items: ProductionKeyWarning[] | null | undefined }) => (
    items && items.length > 0 ? (
        <Stack spacing={1} sx={{ my: 1 }} data-testid="production-key-warning">
            {items.map((item, idx) => <Alert key={`${item.kind}-${idx}`} severity="warning">{item.text}</Alert>)}
        </Stack>
    ) : null
);

/** A vendor check's answer: in the server's words, with what the credentials reach. */
export const ProbeResult = ({ probe, onClose }: { probe: IntegrationProbe; onClose?: () => void }) => {
    const words = PROBE_RESULTS[probe.result] || { label: probe.result, tone: 'warning' };
    const severity = probe.result === 'ok' ? 'success' : probe.result === 'rejected' ? 'error' : 'warning';
    return (
        <Box sx={{ my: 1 }} data-testid="probe-result">
            <Alert severity={severity} onClose={onClose}>
                <AlertTitle sx={{ mb: 0.25 }}>{words.label}</AlertTitle>
                {probe.message}
                {probe.accounts && probe.accounts.length > 0 && (
                    <Box component="span" sx={{ display: 'block' }}>Reaches: {probe.accounts.map((a) => a.name).join(', ')}</Box>
                )}
                <Box component="span" sx={{ display: 'block', typography: 'caption', color: 'text.secondary' }}>Checked {formatTs(probe.checked_at)}</Box>
            </Alert>
            <ProductionKeyWarnings items={probe.production_key} />
        </Box>
    );
};
